import React, { useMemo } from 'react';
import { Box, Typography, Link, Chip } from '@mui/material';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import AppointmentsList from './AppointmentsList';
import { Membership, OrganizationMembership } from '../types/patient';

type RoleChip = {
  key: string;
  label: string;
};

type RoleChipsSectionProps = {
  roleChips: RoleChip[];
  theme: ReturnType<typeof useThemeContext>['theme'];
};

const toTitleCase = (value: string): string =>
  value
    .split('_')
    .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}` : ''))
    .join(' ');

const getRoleChips = (memberships: Membership[], organizationMemberships: OrganizationMembership[]): RoleChip[] => {
  const chips: RoleChip[] = [];
  const seen = new Set<string>();

  const addChip = (scope: 'Account' | 'Org', role: string | undefined, entityName: string | undefined) => {
    if (!role || !entityName) {
      return;
    }

    const key = `${scope}:${role}:${entityName}`;
    if (seen.has(key)) {
      return;
    }

    chips.push({
      key,
      label: `${scope} ${toTitleCase(role)} · ${entityName}`,
    });
    seen.add(key);
  };

  memberships.forEach((membership) => {
    addChip('Account', membership.user_acc_role, membership.employer_name);
  });

  organizationMemberships.forEach((organizationMembership) => {
    addChip('Org', organizationMembership.user_org_role, organizationMembership.org_name);
  });

  return chips;
};

const renderRoleChipsSection = ({ roleChips, theme }: RoleChipsSectionProps): JSX.Element | null => {
  if (roleChips.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        padding: { sm: '0 8px', xs: '0 4px' },
        marginBottom: { xs: '12px', sm: '16px' },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          whiteSpace: 'nowrap',
          color: theme.custom.pages.frost.authHome.typography.body1.color,
        }}
      >
        Your roles:
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        {roleChips.map((chip) => (
          <Chip
            key={chip.key}
            label={chip.label}
            size="small"
            sx={{
              fontWeight: 500,
              border: '1px solid',
              borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
              backgroundColor: theme.custom.pages.frost.authHome.cards.event.backgroundColor,
              color: theme.custom.pages.frost.authHome.typography.body1.color,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const FrostAuthHome = () => {
  const { theme, cognitoUserInfo, memberships, organizationMemberships, isMembershipDataReady } = useThemeContext();

  const loading = !isMembershipDataReady;
  const membershipsToShow = useMemo<Membership[]>(() => memberships ?? [], [memberships]);
  const organizationMembershipsToShow = useMemo<OrganizationMembership[]>(
    () => organizationMemberships ?? [],
    [organizationMemberships],
  );

  const shouldShowMembershipBox = !loading && membershipsToShow.length > 0;
  const roleChips = useMemo<RoleChip[]>(
    () => getRoleChips(membershipsToShow, organizationMembershipsToShow),
    [membershipsToShow, organizationMembershipsToShow],
  );

  const pageBg = theme.layout.defaults.backgroundColor;

  const cardSx = {
    backgroundColor: theme.custom.pages.frost.authHome.cards.backgroundColor,
    borderRadius: '16px',
    padding: { sm: '28px', xs: '20px' },
    border: '1px solid',
    borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
  };

  const contactLinkSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    marginTop: '10px',
    fontWeight: 600,
    textDecoration: 'none',
  };

  const membershipItemSx = {
    border: '1px solid',
    borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
    borderRadius: '14px',
    padding: '16px',
    backgroundColor: theme.custom.pages.frost.authHome.cards.event.backgroundColor,
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: pageBg,
        borderRadius: '20px',
        padding: { xs: '20px', sm: '40px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          padding: { sm: '5px 8px', xs: '10px 4px' },
          alignItems: 'center',
          width: '100%',
          marginBottom: { xs: '10px', sm: '18px' },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: theme.custom.pages.frost.authHome.typography.body1.color,
          }}
        >
          Welcome, {cognitoUserInfo?.attributes?.name}
        </Typography>
      </Box>
      {renderRoleChipsSection({ roleChips, theme })}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: { xs: '18px', md: '28px' },
          alignSelf: 'stretch',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <AppointmentsList limit={3} />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: { xs: '18px', md: '20px' } }}>
          <Box sx={cardSx}>
            <Typography
              variant="h6"
              marginBottom="14px"
              sx={{ color: theme.custom.pages.frost.authHome.typography.body1.color, fontWeight: 700 }}
            >
              Health Care Provider
            </Typography>
            <Box sx={membershipItemSx}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.custom.pages.frost.authHome.typography.body1.color,
                  marginTop: '6px',
                }}
              >
                FROST
              </Typography>

              <Link href="tel:+18314772867" color="primary" underline="hover" sx={contactLinkSx}>
                <PhoneIcon fontSize="small" sx={{ opacity: 0.9 }} />
                +1 (831) 477-2867
              </Link>

              <Link href="mailto:drcasey@fastresponseonsite.com" color="primary" underline="hover" sx={contactLinkSx}>
                <MailIcon fontSize="small" sx={{ opacity: 0.9 }} />
                drcasey@fastresponseonsite.com
              </Link>
            </Box>
          </Box>

          {shouldShowMembershipBox ? (
            <Box sx={cardSx}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: theme.custom.pages.frost.authHome.typography.body1.color }}
              >
                Membership Info
              </Typography>

              <Box sx={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {membershipsToShow.map((m, idx) => {
                  const phone = (m?.employer_contact_phone || '').trim();
                  const email = (m?.employer_contact_email || '').trim();

                  return (
                    <Box key={`${m?.org_id ?? 'org'}-${m?.acc_id ?? idx}-membership`} sx={membershipItemSx}>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.custom.pages.frost.authHome.typography.body1.color }}
                      >
                        Employer: {m?.employer_name || '-'}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ marginTop: '6px', color: theme.custom.pages.frost.authHome.typography.body1.color }}
                      >
                        Employer Contact: {m?.employer_contact_name || '-'}
                      </Typography>

                      {phone ? (
                        <Link
                          href={`tel:${phone}`}
                          color="primary"
                          underline="hover"
                          sx={{ ...contactLinkSx, marginTop: '10px' }}
                        >
                          <PhoneIcon fontSize="small" sx={{ opacity: 0.9 }} />
                          {phone}
                        </Link>
                      ) : null}

                      {email ? (
                        <Link
                          href={`mailto:${email}`}
                          color="primary"
                          underline="hover"
                          sx={{ ...contactLinkSx, marginTop: '8px' }}
                        >
                          <MailIcon fontSize="small" sx={{ opacity: 0.9 }} />
                          {email}
                        </Link>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default FrostAuthHome;
